import React, { useEffect, useState, forwardRef, useCallback, useImperativeHandle } from 'react';
import { organizationsApi, robotsApi } from "api";
import { Robot } from "hooks/useRobots";
import {
    CircularProgress,
    Stack,
    Typography,
    IconButton,
    Dialog,
    DialogActions,
    DialogTitle,
    Box,
    Tooltip,
    useTheme,
} from '@mui/material';
import { Add, Clear, PrecisionManufacturing, Info, ErrorOutline, Edit } from '@mui/icons-material';
import AvatarRem from './AvatarRem';
import { getTranslation, OutletType } from 'common';
import { useBreakpoint, useTranslations, useUser } from 'hooks';
import RobotDetails from './RobotDetails';
import { useOutletContext } from 'react-router-dom';
import { useSnackbar } from 'hooks';
import RobotPropertiesDialog from './dialogs/RobotPropertiesDialog';
import OrganizationResourceTable, {
    OrganizationResourceCards,
    OrganizationResourceColumn,
} from 'pages/organizations/OrganizationResources';
import FlowentiButton from './flowenti-ui-kit/FlowentiButton';
import noRobotsImage from "image/empty-robots.png";
import noRobotsImageDark from "image/empty-robots-dark.png";
import { SupportedUserDataTypes } from 'hooks/useUserDataKeys';
interface RobotsPanelProps {
    organizationId: number;
    administrator: boolean;
    onOpenRobotsShop?: () => void;
}
interface Category {
    id: number;
    description: string;
}

type Ref = { getRobots: Function } | undefined;

const RobotsPanel = forwardRef<Ref, RobotsPanelProps>(({ organizationId, administrator, onOpenRobotsShop }, ref) => {
    const { secondFilter: robotsFilter } = useOutletContext<OutletType>();
    const [robots, setRobots] = useState<Robot[]>([]);
    const [loading, setLoading] = useState(false);
    const [robotToRemove, setRobotToRemove] = useState<Robot | undefined>(undefined);
    const [selectedRobot, setSelectedRobot] = useState<Robot | undefined>(undefined);
    const [robotToEdit, setRobotToEdit] = useState<Robot | undefined>(undefined);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [editValuesOpen, setEditValuesOpen] = useState(false);
    const translations = useTranslations();
    const [categories, setCategories] = useState<Category[]>([]);
    const { sendSnack } = useSnackbar();
    const user = useUser();
    const isMobile = useBreakpoint("down", "sm");
    const theme = useTheme();
    const emptyRobotsImage = theme.palette.mode === "dark"
        ? noRobotsImageDark
        : noRobotsImage;

    const getCategoryDescription = (id: number) => {
        const category = categories.find(category => category.id === id);
        return category ? category.description : '';
    };

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const { data } = await robotsApi.get('/categories');
                if (data) {
                    setCategories(data.categories);
                }
            } catch {
                setCategories([]);
            }
        };
        fetchCategories();
    }, []);

    const fetchRobots = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await organizationsApi.get(`/${organizationId}/robots`);
            setRobots(data.robots);
        } catch { }
        finally {
            setLoading(false);
        }
    }, [organizationId]);

    useEffect(() => {
        fetchRobots();
    }, [fetchRobots])

    const handleRemoveRobot = async () => {
        if (robotToRemove) {
            try {
                const response = await organizationsApi.delete(`/${organizationId}/robots/${robotToRemove.id}`);
                setRobots((prevRobots) => prevRobots.filter(robot => robot.id !== robotToRemove.id));
                sendSnack({
                    message: response.data.responsemessage,
                    type: 'success',
                });
                handleCloseDialog();
            } catch { }
        }
    };

    const handleOpenDialog = (robot: Robot) => {
        setRobotToRemove(robot);
    };

    const handleCloseDialog = () => {
        setRobotToRemove(undefined);
    };

    const handleOpenDetails = (robot: Robot) => {
        setSelectedRobot(robot);
        setDetailsOpen(true);
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedRobot(undefined);
    };

    const handleOpenEditValues = (robot: Robot) => {
        setRobotToEdit(robot);
        setEditValuesOpen(true);
    };

    const handleCloseEditValues = () => {
        setEditValuesOpen(false);
        setRobotToEdit(undefined);
    };

    useImperativeHandle(
        ref,
        () => ({
            getRobots: fetchRobots,
        }),
        [fetchRobots]
    );

    const filteredRobots = robots.filter((robot) =>
        robot.name.toLowerCase().includes(robotsFilter.toLowerCase())
    );

    const columns: OrganizationResourceColumn<Robot>[] = [
        {
            id: 'name',
            label: 'Robot',
            sortType: SupportedUserDataTypes.string,
            sortValue: (robot) => robot.name || "",
            render: (robot) => (
                <Stack direction="row" alignItems="center" spacing={2}>
                    <AvatarRem icon={robot.picture || ""}>
                        <PrecisionManufacturing />
                    </AvatarRem>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography fontWeight={700} noWrap>
                                {robot.name}
                            </Typography>
                            {robot.published === false && robot.user?.id !== user?.userId && (
                                <Tooltip title={getTranslation(translations, "robot.deprecated")}>
                                    <ErrorOutline fontSize="small" color="warning" />
                                </Tooltip>
                            )}
                        </Stack>
                        {robot.description && (
                            <Typography variant="body2" color="text.secondary" noWrap>
                                {robot.description}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            ),
        },
        {
            id: 'category',
            label: getTranslation(translations, "generic.category"),
            sortType: SupportedUserDataTypes.string,
            sortValue: (robot) => robot.idcategory ? getCategoryDescription(robot.idcategory) : "",
            render: (robot) => (
                <Typography color="text.secondary">
                    {robot.idcategory ? getCategoryDescription(robot.idcategory) : "-"}
                </Typography>
            ),
        },
        {
            id: 'actions',
            label: getTranslation(translations, "generic.actions"),
            align: 'right',
            render: (robot) => (
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    {administrator && (
                        <Tooltip title={getTranslation(translations, "robot.edit.values")}>
                            <IconButton
                                id='robot-edit-values-icon'
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenEditValues(robot);
                                }}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title={getTranslation(translations, "robot.details.tooltip")}>
                        <IconButton
                            id='robot-info-icon'
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDetails(robot);
                            }}
                        >
                            <Info fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {administrator && (
                        <Tooltip title={getTranslation(translations, "generic.remove")}>
                            <IconButton
                                id='remove-robot-from-organization'
                                size='small'
                                edge="end"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDialog(robot);
                                }}
                            >
                                <Clear />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            ),
        },
    ];

    return (
        <Stack height="100%" overflow="auto" flex={1}>
            {loading ? (
                <Stack alignItems="center">
                    <CircularProgress />
                </Stack>
            ) : filteredRobots.length === 0 ? (
                <Stack height="100%" alignItems="center" justifyContent="center">
                    <Box
                        component="img"
                        src={emptyRobotsImage}
                        alt="non ci sono robot"
                        sx={{ width: 400, height: 400, objectFit: "contain" }}
                    />
                    <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" spacing={1.5}>
                        <Typography variant="h6">{getTranslation(translations, "generic.no.robots")}</Typography>
                        {administrator && onOpenRobotsShop && (
                            <FlowentiButton
                                id="btn-empty-robots-shop"
                                size="small"
                                variant="contained"
                                startIcon={<Add />}
                                onClick={onOpenRobotsShop}
                            >
                                {getTranslation(translations, "robots.name")}
                            </FlowentiButton>
                        )}
                    </Stack>
                </Stack>
            ) : (
                isMobile ? (
                    <OrganizationResourceCards
                        id="robots-list"
                        rows={filteredRobots}
                        columns={columns}
                        getRowKey={(robot) => robot.id}
                        getRowId={(robot) => `robot-${robot.id}`}
                        canHover={() => administrator}
                        onRowClick={(robot) => {
                            if (administrator) {
                                handleOpenEditValues(robot);
                            }
                        }}
                    />
                ) : (
                    <OrganizationResourceTable
                        id="robots-list"
                        rows={filteredRobots}
                        columns={columns}
                        getRowKey={(robot) => robot.id}
                        getRowId={(robot) => `robot-${robot.id}`}
                        canHover={() => administrator}
                        onRowClick={(robot) => {
                            if (administrator) {
                                handleOpenEditValues(robot);
                            }
                        }}
                    />
                )
            )}
            {/* Dialog di conferma rimozione */}
            <Dialog
                open={!!robotToRemove}
                onClose={handleCloseDialog}
            >
                <DialogTitle>
                    {getTranslation(translations, "confirm.remove.robot.title")}
                </DialogTitle>
                <DialogActions>
                    <FlowentiButton variant="outlined" size="small" onClick={handleCloseDialog}>
                        {getTranslation(translations, "generic.button.cancel")}
                    </FlowentiButton>
                    <FlowentiButton
                        onClick={handleRemoveRobot}
                        autoFocus
                        id='confirm-remove-robot'
                        variant="contained"
                        size="small"
                    >
                        {getTranslation(translations, "generic.remove")}
                    </FlowentiButton>
                </DialogActions>
            </Dialog>
            {/* Dialog dettagli robot */}
            <RobotDetails
                open={detailsOpen}
                onClose={handleCloseDetails}
                robot={selectedRobot}
                onRobotUpdate={fetchRobots}
            />
            {/* Dialog modifica valori robot */}
            <RobotPropertiesDialog
                open={editValuesOpen}
                onClose={handleCloseEditValues}
                robot={robotToEdit}
                onUpdate={fetchRobots}
            />
        </Stack>
    );
});

export default RobotsPanel;
